export const API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '/api');

class ApiService {
  async request(endpoint, options = {}) {
    const url = endpoint.startsWith('http')
      ? endpoint
      : `${API_BASE_URL}${endpoint}`;

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Inject Auth Token
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Preserve server error message from JSON body
        let serverMsg = `HTTP error! status: ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody?.error?.message) serverMsg = errBody.error.message;
          else if (errBody?.message) serverMsg = errBody.message;
          else if (errBody?.detail) serverMsg = errBody.detail;
        } catch {}
        const err = new Error(serverMsg);
        err.status = response.status;
        throw err;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return await response.text();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get(endpoint, params = {}) {
    const qs = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const val = params[key];
      if (val !== undefined && val !== null) qs.append(key, val);
    });
    const path = qs.toString() ? `${endpoint}?${qs.toString()}` : endpoint;
    return this.request(path, { method: 'GET' });
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: data,
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data,
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Auth
  async login(email, password) {
    const base = (API_BASE_URL || '/api').replace(/\/$/, '');
    const response = await fetch(`${base}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.error?.message || data?.message || `Login failed: ${response.status}`);
    return data;
  }

  async register(email, username, password, full_name = '') {
    const base = (API_BASE_URL || '/api').replace(/\/$/, '');
    const response = await fetch(`${base}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, username, password, full_name }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.error?.message || `Registration failed: ${response.status}`);
    return data;
  }

  async getMe() {
    return this.get('/auth/me');
  }

  // Flows CRUD
  async getFlows() {
    try { return await this.get('/flows'); } catch (err) {
      if (err?.status === 401) throw err;
      return [];
    }
  }

  async createFlow(name, nodes = [], edges = [], description = '') {
    return this.post('/flows', { name, nodes, edges, description });
  }

  async updateFlow(flowId, updates) {
    return this.request(`/flows/${flowId}`, { method: 'PUT', body: updates });
  }

  async deleteFlow(flowId) {
    return this.delete(`/flows/${flowId}`);
  }

  async saveFlow(flowId, name, nodes, edges) {
    if (flowId) {
      try {
        return await this.updateFlow(flowId, { name, nodes, edges });
      } catch (err) {
        // Only fall through to create if it's a 404 (flow was deleted)
        // Re-throw auth errors so the caller knows the token is invalid
        if (err?.message && (err.message.includes('401') || err.message.includes('Authentication') || err.message.includes('token'))) throw err;
        if (!err?.message?.includes('404')) throw err;
        // Flow was deleted — create a new one
      }
    }
    return this.createFlow(name, nodes, edges);
  }

  // Embed management
  async createEmbed(flowId, config = {}) {
    return this.post(`/flows/${flowId}/embeds`, config);
  }

  async getEmbeds(flowId) {
    try { return await this.get(`/flows/${flowId}/embeds`); } catch { return []; }
  }

  async deleteEmbed(embedId) {
    return this.delete(`/embeds/${embedId}`);
  }

  // AI image generation via CF Workers AI
  async generateImage(prompt) {
    return this.post('/ai/image', { prompt });
  }

  // Run a single flow node via CF Workers AI
  async runNode(nodeType, config, input) {
    return this.post('/ai/run-node', { nodeType, config, input });
  }

  // AI model suggestions using Workers AI
  async getAISuggestions(nodeType, currentConfig, context = '') {
    const defaults = {
      llm: [
        'You are a helpful assistant that provides clear and concise answers.',
        'Act as an expert in the given topic and provide detailed explanations.',
        'Respond in a friendly and conversational tone.',
        'Focus on practical, actionable advice.',
      ],
      imageGen: [
        'A photorealistic image of',
        'Digital art style, vibrant colors',
        'Minimalist design with clean lines',
        'Fantasy art with magical elements',
      ],
      textAnalysis: [
        'Analyze sentiment and emotional tone',
        'Extract key entities and topics',
        'Summarize main points',
        'Identify potential issues or concerns',
      ],
    };

    try {
      const base = (API_BASE_URL || '/api').replace(/\/$/, '');
      const prompt = `Generate 4-5 helpful configuration suggestions for a "${nodeType}" node in an AI workflow builder. Current config: ${JSON.stringify(currentConfig || {})}. Context: ${context || 'general'}. Return a JSON array of strings only.`;

      const response = await fetch(`${base}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: prompt,
          systemPrompt: 'You are an AI flow configuration expert. Return only a JSON array of strings with no other text.'
        }),
        signal: AbortSignal.timeout(5000)
      });

      if (!response.ok) return { suggestions: defaults[nodeType] || [], context_aware: [] };

      const data = await response.json();
      const aiOutput = data.reply || '';

      let suggestions = [];
      try {
        const jsonMatch = aiOutput.match(/\[.*\]/s);
        suggestions = jsonMatch ? JSON.parse(jsonMatch[0]) : (defaults[nodeType] || []);
      } catch {
        suggestions = defaults[nodeType] || [];
      }

      return {
        suggestions,
        context_aware: context ? [`Based on "${context}": ${suggestions[0] || ''}`] : [],
      };
    } catch {
      return { suggestions: defaults[nodeType] || [], context_aware: [] };
    }
  }

  // Helper to check if backend is available
  async checkBackendHealth() {
    try {
      const base = (API_BASE_URL || '/api').replace(/\/$/, '');
      const response = await fetch(`${base}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000)
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  // Template management
  async saveTemplate(template) {
    try {
      return await this.post('/templates', template);
    } catch {
      const templates = JSON.parse(localStorage.getItem('ai-flow-templates') || '[]');
      const newTemplate = { ...template, id: Date.now().toString(), created_at: new Date().toISOString() };
      templates.push(newTemplate);
      localStorage.setItem('ai-flow-templates', JSON.stringify(templates));
      return newTemplate;
    }
  }

  async getTemplates() {
    try {
      const base = (API_BASE_URL || '/api').replace(/\/$/, '');

      // Try backend API first
      try {
        const response = await fetch(`${base}/templates`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(3000)
        });

        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await response.json();
          }
        }
      } catch (backendError) {
        // Backend unavailable - silently fall back to localStorage
        if (import.meta.env.DEV && backendError.name !== 'AbortError') {
          console.debug('Backend template API not available, using localStorage fallback');
        }
      }

      // Fallback to localStorage if backend unavailable
      const templates = JSON.parse(localStorage.getItem('ai-flow-templates') || '[]');
      return templates;
    } catch (error) {
      // Return empty array on any error
      return [];
    }
  }

  async deleteTemplate(templateId) {
    try {
      return await this.delete(`/templates/${templateId}`);
    } catch {
      const templates = JSON.parse(localStorage.getItem('ai-flow-templates') || '[]');
      localStorage.setItem('ai-flow-templates', JSON.stringify(templates.filter(t => t.id !== templateId)));
      return true;
    }
  }

  // Flow sharing
  async exportFlow(flow, metadata = {}) {
    const exportData = {
      version: '1.0.0',
      exported_at: new Date().toISOString(),
      metadata: {
        name: metadata.name || 'Untitled Flow',
        description: metadata.description || '',
        author: metadata.author || 'Anonymous',
        ...metadata,
      },
      flow,
    };

    return exportData;
  }

  async importFlow(importData) {
    // Validate import data
    if (!importData.flow || !importData.flow.nodes || !importData.flow.edges) {
      throw new Error('Invalid flow data format');
    }

    // Validate version compatibility
    if (importData.version && importData.version !== '1.0.0') {
      console.warn(`Flow version mismatch: ${importData.version} vs 1.0.0, attempting to import anyway`);
    }

    // Validate flow structure
    if (!Array.isArray(importData.flow.nodes) || !Array.isArray(importData.flow.edges)) {
      throw new Error('Invalid flow structure: nodes and edges must be arrays');
    }

    // Validate node structure
    for (const node of importData.flow.nodes) {
      if (!node.id || !node.type) {
        throw new Error('Invalid node structure: each node must have id and type');
      }
    }

    // Validate edge structure
    for (const edge of importData.flow.edges) {
      if (!edge.source || !edge.target) {
        throw new Error('Invalid edge structure: each edge must have source and target');
      }
    }

    return importData.flow;
  }

  // Dashboard endpoints — all fall back to localStorage when backend is unavailable

  async getDashboard() {
    try {
      return await this.get('/dashboard');
    } catch {
      const teams    = JSON.parse(localStorage.getItem('ai-flow-teams')    || '[]');
      const projects = JSON.parse(localStorage.getItem('ai-flow-projects') || '[]');
      const goals    = JSON.parse(localStorage.getItem('ai-flow-goals')    || '[]');
      const avgProgress = goals.length
        ? Math.round(goals.reduce((s, g) => s + (Number(g.progress) || 0), 0) / goals.length)
        : 0;
      return {
        stats: { active_users: 1, projects: projects.length, goals_progress: avgProgress, executions: 0 },
        teams,
        projects,
        goals,
      };
    }
  }

  async listTeams() {
    try {
      return await this.get('/teams');
    } catch {
      return JSON.parse(localStorage.getItem('ai-flow-teams') || '[]');
    }
  }

  async createTeam(payload) {
    try {
      return await this.post('/teams', payload);
    } catch {
      const team = { id: `team_${Date.now()}`, members: 0, ...payload };
      const teams = JSON.parse(localStorage.getItem('ai-flow-teams') || '[]');
      teams.unshift(team);
      localStorage.setItem('ai-flow-teams', JSON.stringify(teams));
      return team;
    }
  }

  async listProjects() {
    try {
      return await this.get('/projects');
    } catch {
      return JSON.parse(localStorage.getItem('ai-flow-projects') || '[]');
    }
  }

  async createProject(payload) {
    try {
      return await this.post('/projects', payload);
    } catch {
      const project = { id: `proj_${Date.now()}`, status: 'in_progress', ...payload };
      const projects = JSON.parse(localStorage.getItem('ai-flow-projects') || '[]');
      projects.unshift(project);
      localStorage.setItem('ai-flow-projects', JSON.stringify(projects));
      return project;
    }
  }

  async listGoals() {
    try {
      return await this.get('/goals');
    } catch {
      return JSON.parse(localStorage.getItem('ai-flow-goals') || '[]');
    }
  }

  async createGoal(payload) {
    try {
      return await this.post('/goals', payload);
    } catch {
      const goal = { id: `goal_${Date.now()}`, progress: 0, ...payload };
      const goals = JSON.parse(localStorage.getItem('ai-flow-goals') || '[]');
      goals.unshift(goal);
      localStorage.setItem('ai-flow-goals', JSON.stringify(goals));
      return goal;
    }
  }

  async updateGoal(goalId, payload) {
    return this.put(`/goals/${goalId}`, payload);
  }

  async getProject(projectId) {
    return this.get(`/projects/${projectId}`);
  }

  async updateProject(projectId, payload) {
    return this.put(`/projects/${projectId}`, payload);
  }

  async getEmbed(embedId) {
    return this.get(`/embeds/${embedId}`);
  }

  // Project flow helpers
  async createProjectWithFlow({ name, due_date, status = 'in_progress' }, flow) {
    return this.createProject({ name, due_date, status, flow });
  }
}

export default new ApiService();
